<template>
  <div>
    <inventory-edit
      ref="inventoryEditRef"
      @save-success="editInventorySaveSuccess"
    />
    <v-container fluid class="pa-4">
      <div
        v-if="authPermissions.has('inventory_monitoring:view')"
        class="flex flex-col"
        style="gap: 16px"
      >
        <!-- Summary + Chart Card -->
        <v-card outlined>
          <v-card-title class="py-3 px-4">
            <v-icon left color="primary">
              mdi-chart-bar
            </v-icon>
            <span class="text-subtitle-1 font-weight-bold">Most Sold Items</span>
            <v-spacer />
            <!-- Today Sales badge -->
            <div class="d-flex align-center" style="gap: 8px">
              <span class="text-caption grey--text text--darken-1">Today's Sales:</span>
              <v-chip color="success" dark small>
                {{ todaySales }}
              </v-chip>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text class="pb-2">
            <!-- Filter row -->
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
              <v-text-field
                type="date"
                dense
                hide-details
                :value="mostSoldItemsDateRange[0]"
                outlined
                label="Date Start"
                style="max-width: 180px"
                @change="dateStartChange"
              />
              <v-text-field
                type="date"
                dense
                hide-details
                :value="mostSoldItemsDateRange[1]"
                outlined
                label="Date End"
                style="max-width: 180px"
                @change="dateEndChange"
              />
              <v-select
                v-model="mostSoldItemsLimit"
                label="No. of Items"
                dense
                hide-details
                :items="[5, 6, 7, 8]"
                outlined
                style="max-width: 130px"
              />
              <v-btn color="primary" small @click="getMostSoldItems()">
                <v-icon left small>
                  mdi-refresh
                </v-icon>
                Refresh
              </v-btn>
            </div>
          </v-card-text>
          <!-- Chart -->
          <div style="position: relative; height: 380px; padding: 0 16px 16px">
            <most-sold-items-chart
              ref="mostSoldItemsChartRef"
              :data="mostSoldItemsChartData"
              style="width: 100%; height: 100%; position: absolute"
            />
          </div>
        </v-card>

        <!-- Inventory Table -->
        <inventory-table
          ref="inventoryTableRef"
          @edit="editInventory"
        />
      </div>
    </v-container>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import {
  ErrorPopup,
  firstDayOfMonth,
  formatAccounting,
  useWatchEffect,
} from '../../../lib/utils'
import appAxios from '../../../extra-functions/axios.extra'

const [watchEffect, clearWatchEffect] = useWatchEffect()

export default {
  name: 'InventoryMonitoring',
  components: {
    inventoryEdit: () =>
      import('@/views/dashboard/components/inventory-monitoring/InventoryEdit'),
    inventoryTable: () =>
      import(
        '@/views/dashboard/components/inventory-monitoring/InventoryTable'
      ),
    mostSoldItemsChart: () =>
      import('@/views/dashboard//component/MostSoldItemsChart.vue'),
  },
  data() {
    return {
      fetchingMostSoldItems: false,
      mostSoldItemsLimit: 5,
      mostSoldItemsDateRange: [firstDayOfMonth(), null],
      mostSoldItemsChartData: {
        labels: [],
        backgroundColor: [],
        data: [],
        datasetLabel: '',
      },
      todaySales: '0.00',
    }
  },
  computed: {
    ...mapState({
      axios: (state) => appAxios(state.auth.token),
      storeId: (state) => state.settings.storeId,
      authPermissions: (state) => state.auth.userPermissions,
    }),
  },
  created() {
    this.getTotalTodaySales()
  },
  mounted() {
    this.getMostSoldItems()
  },
  destroyed() {
    clearWatchEffect()
  },
  methods: {
    editInventory(inventoryId) {
      this.$refs?.inventoryEditRef?.open?.(inventoryId)
    },
    editInventorySaveSuccess() {
      this.$refs?.inventoryTableRef?.getIndex?.()
    },
    getMostSoldItems() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          this.fetchingMostSoldItems = true
          const { data } = await this.axios.get(
            `/stocks/most-sold-items/${this.storeId}`,
            {
              params: {
                dateStart: this.mostSoldItemsDateRange[0],
                dateEnd: this.mostSoldItemsDateRange[1],
                limit: this.mostSoldItemsLimit,
              },
            },
          )
          this.mostSoldItemsChartData = {
            labels: data.labels,
            data: data.quantities,
            datasetLabel: data.dateRange,
            backgroundColor: data.colors,
          }
          this.$refs.mostSoldItemsChartRef.update()
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingMostSoldItems = false
        }
      }, 'getMostSoldItems')
    },
    dateStartChange(date) {
      this.mostSoldItemsDateRange[0] = date
      this.mostSoldItemsDateRange = [...this.mostSoldItemsDateRange]
    },
    dateEndChange(date) {
      this.mostSoldItemsDateRange[1] = date
      this.mostSoldItemsDateRange = [...this.mostSoldItemsDateRange]
    },
    async getTotalTodaySales() {
      try {
        const { data } = await this.axios.get(
          '/v2/sale-inventories/today-sales',
        )
        this.todaySales = formatAccounting(data)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error(e)
        }
        ErrorPopup.fire({
          title: 'Error',
          text: 'Failed to fetch total today sales',
        })
      }
    },
  },
}
</script>
